import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, FileText, Home, Building2, MapPin, Ruler, Eye } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const PlanningDrawings = () => {
  const faqs = [
    {
      question: "What planning drawings do I need?",
      answer: "Most planning applications require: location plan (1:1250 or 1:2500 showing the site in context), site plan/block plan (1:500 or 1:200 showing the plot), existing and proposed floor plans, existing and proposed elevations, and sometimes sections or 3D visualisations. Requirements vary by project type and local authority."
    },
    {
      question: "Do you submit the planning application?",
      answer: "We focus on producing accurate drawings to support your application. We can advise on what drawings you need and ensure they meet local authority requirements, but we recommend working with a planning consultant or architect for the application submission and planning strategy itself."
    },
    {
      question: "How much do planning drawings cost?",
      answer: "Costs depend on project complexity. Simple extensions from £300, house alterations from £400, new build houses from £600. We provide fixed-price quotes based on your specific requirements. Location plans and site plans are often included or available as add-ons."
    },
    {
      question: "What information do you need from me?",
      answer: "We need: property address (for location/site plans), existing plans or a measured survey, your design brief/intentions, any design sketches you have, and local authority pre-application advice if available. Photos of the property are also helpful."
    },
    {
      question: "How long does it take?",
      answer: "Standard turnaround is 5-10 working days depending on complexity. Simple projects can be faster; large or complex schemes may take longer. We'll confirm timescales when quoting. Rush service available for urgent deadlines."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Planning Drawings | Planning Application Plans | Location Plans | Site Plans | Energy Performance Direct</title>
        <meta name="description" content="Professional planning drawings for planning applications. Floor plans, elevations, location plans, site plans. Extensions, alterations, new builds. From £300. Call 01392 980077." />
        <meta name="keywords" content="planning drawings, planning application drawings, location plan, site plan, floor plans planning, elevations planning, extension plans, householder planning" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/planning-drawings" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <section className="py-16 bg-gradient-to-br from-teal-50 via-cyan-50 to-teal-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-teal-600">Planning Application Support</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Planning Drawings
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Professional drawings for planning applications. Floor plans, elevations, sections, location plans and site plans for extensions, alterations and new builds.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-sm font-medium">From £300</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-sm font-medium">To Scale</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-sm font-medium">LPA Compliant</span>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/booking">
                  <Button size="lg" className="bg-teal-600 hover:bg-teal-700">
                    Request Planning Quote
                  </Button>
                </Link>
                <a href="tel:01392980077">
                  <Button size="lg" variant="outline" className="border-teal-600 text-teal-600 hover:bg-teal-50">
                    <Phone className="mr-2 h-5 w-5" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Planning Drawing Services</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <MapPin className="h-12 w-12 text-teal-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Location Plans</h3>
                    <p className="text-gray-600">
                      OS-based location plans at 1:1250 or 1:2500 scale showing your site in its wider context. Red line boundary clearly marked. Essential for all planning applications.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Ruler className="h-12 w-12 text-teal-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Site Plans</h3>
                    <p className="text-gray-600">
                      Detailed site/block plans at 1:500 or 1:200 showing the property, boundaries, access, parking, landscaping and relationship to neighbours.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <FileText className="h-12 w-12 text-teal-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Floor Plans</h3>
                    <p className="text-gray-600">
                      Existing and proposed floor plans at 1:100 or 1:50 scale. Room layouts, dimensions, door/window positions, stairs and key features clearly shown.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Eye className="h-12 w-12 text-teal-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Elevations</h3>
                    <p className="text-gray-600">
                      Existing and proposed elevations showing all external faces. Materials annotated, heights dimensioned, relationship to ground levels shown.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Home className="h-12 w-12 text-teal-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Sections</h3>
                    <p className="text-gray-600">
                      Cross-sections through the building showing internal heights, floor levels, roof construction and relationship between spaces.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Building2 className="h-12 w-12 text-teal-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">3D Visualisations</h3>
                    <p className="text-gray-600">
                      3D views and renders to help visualise proposals and support applications, particularly useful for sensitive sites or design-focused authorities.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Project Types</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-white">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-lg mb-4">Householder Applications</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li>• Single and double storey extensions</li>
                      <li>• Loft conversions with dormers</li>
                      <li>• Garage conversions</li>
                      <li>• Outbuildings and garden rooms</li>
                      <li>• Porches and conservatories</li>
                      <li>• External alterations</li>
                    </ul>
                    <p className="mt-4 text-sm text-teal-600 font-medium">From £300</p>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-lg mb-4">Full Planning Applications</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li>• New build houses</li>
                      <li>• Change of use</li>
                      <li>• Barn conversions</li>
                      <li>• Commercial alterations</li>
                      <li>• Sub-divisions</li>
                      <li>• Mixed-use developments</li>
                    </ul>
                    <p className="mt-4 text-sm text-teal-600 font-medium">From £600</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Planning Drawings FAQs</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card key={index}>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-teal-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Need Planning Drawings?</h2>
            <p className="text-xl mb-8 opacity-90">
              Professional drawings for your planning application
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/booking">
                <Button size="lg" variant="secondary" className="bg-white text-teal-600 hover:bg-gray-100">
                  Request Quote
                </Button>
              </Link>
              <a href="tel:01392980077">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-teal-700">
                  <Phone className="mr-2 h-5 w-5" />
                  01392 980077
                </Button>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default PlanningDrawings;
